import type { ApiErrorBody, TokenResponse } from '@field-support/shared';
import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { ApiError, report } from './errors';
import { getAccessToken, notifySessionExpired, setAccessToken } from './token-store';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    /** Set on the one retry a 401 earns after a successful token refresh. */
    _retried?: boolean;
  }
}

const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
const BASE_URL = `${API_ORIGIN}/api/v1`;
/**
 * Cross-origin deployments need `include` so the refresh cookie is sent.
 * Same-origin leaves it undefined: the fetch adapter then defaults to
 * 'same-origin', which sends the cookie without allowing it cross-site.
 */
const WITH_CREDENTIALS = API_ORIGIN ? true : undefined;

export type QueryValue = string | number | boolean | null | undefined;

/** Skips undefined/null/'' values and preserves insertion order. */
export function serializeParams(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.append(key, String(value));
  }
  return search.toString();
}

/** The Axios instance every API call goes through (fetch adapter; XHR only for uploads). */
export const http = axios.create({
  adapter: 'fetch',
  baseURL: BASE_URL,
  withCredentials: WITH_CREDENTIALS,
  headers: { Accept: 'application/json' },
  paramsSerializer: { serialize: serializeParams },
});

// Refresh goes through a bare instance: no interceptors, so a 401 from
// /auth/refresh can never recurse into another refresh.
const bare = axios.create({
  adapter: 'fetch',
  baseURL: BASE_URL,
  withCredentials: WITH_CREDENTIALS,
  headers: { Accept: 'application/json' },
});

let refreshInFlight: Promise<TokenResponse | null> | null = null;

/** Uses the refresh cookie to obtain a new access token. Concurrent callers share one request. */
export function refreshSession(): Promise<TokenResponse | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await bare.post<TokenResponse>('/auth/refresh', {});
      setAccessToken(response.data.accessToken);
      return response.data;
    } catch (error) {
      // A rejected refresh (4xx/5xx) ends the session; a network error leaves it undecided.
      if (axios.isAxiosError(error) && error.response) setAccessToken(null);
      return null;
    } finally {
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

function toApiError(response: AxiosResponse): ApiError {
  const data: unknown = response.data;
  const body = (typeof data === 'object' && data !== null ? data : null) as Partial<ApiErrorBody> | null;
  const error = body?.error;
  return report(
    new ApiError(response.status, error?.code ?? 'HTTP_ERROR', error?.message ?? `Request failed (${response.status})`, error?.details),
  );
}

function isAuthEndpoint(url: string | undefined): boolean {
  return Boolean(url && /^\/auth\/(login|refresh|logout)/.test(url));
}

http.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

http.interceptors.response.use(undefined, async (error: unknown) => {
  // Cancellation is the caller's business (TanStack Query aborts stale queries), never an ApiError.
  if (axios.isCancel(error)) throw error;
  if (!axios.isAxiosError(error)) throw error;
  const { config, response } = error;
  if (!response) {
    // No response at all: server down, wrong address, firewall, or a CORS block
    // (the browser hides which). The console gets the browser's own reason.
    throw report(new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the server'), error);
  }
  const apiError = toApiError(response);
  if (response.status === 401 && config && !isAuthEndpoint(config.url)) {
    if (config._retried) {
      // The retried request is still unauthorized: the session is over.
      notifySessionExpired();
      throw apiError;
    }
    const session = await refreshSession();
    if (!session) {
      notifySessionExpired();
      throw apiError;
    }
    config._retried = true;
    return http.request(config as InternalAxiosRequestConfig);
  }
  throw apiError;
});

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

/** Typed JSON request to the API. Refreshes once on 401 and throws ApiError on failure. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  // The backend refuses a write with no JSON body (415), so an action POST with
  // nothing to say still sends `{}`. DELETE carries no body.
  const sendsBody = method === 'POST' || method === 'PUT' || method === 'PATCH';
  const response = await http.request<T>({
    url: path,
    method,
    data: options.body === undefined && sendsBody ? {} : options.body,
    params: options.query,
    signal: options.signal,
  });
  if (response.status === 204 || response.data === '' || response.data == null) return undefined as T;
  return response.data;
}
